import { Box, IconButton, Tooltip } from "@mui/material"
import { ContentCopyOutlined, HelpOutlined } from "@mui-symbols-material/w300"
import { useCopyToClipboard } from "@uidotdev/usehooks"
import { type FC, type MouseEvent } from "react"
import { twMerge } from "tailwind-merge"

import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface GoodsReceiptValueOrMultipleProps {
  values: string[]
  className?: string
  emptyValue?: string
  "data-testid"?: string
}

export const GoodsReceiptValueOrMultiple: FC<GoodsReceiptValueOrMultipleProps> = ({
  values,
  className,
  emptyValue = "",
  "data-testid": testId,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [, copyToClipboard] = useCopyToClipboard()

  const handleStopPropagation = (event: MouseEvent<HTMLElement>) => event.stopPropagation()

  const handleCopy = () => {
    copyToClipboard(values.join(", "))
    notify({
      type: NotificationType.Info,
      message: "Copied to clipboard",
    })
  }

  if (values.length === 0) {
    return (
      <Box component='span' className={className}>
        {emptyValue}
      </Box>
    )
  }

  if (values.length === 1) {
    return (
      <Box component='span' className={className}>
        {values[0]}
      </Box>
    )
  }

  return (
    <Box
      component='span'
      className={twMerge("inline-flex items-center", className)}
      data-testid={testId}
      onClick={handleStopPropagation}
    >
      Multiple
      <Tooltip
        title={
          <Box className='flex items-end gap-x-3 py-1 text-sm font-normal'>
            <Box component='span' className='flex flex-col'>
              {values.map((value) => (
                <Box component='span' key={value}>
                  {value}
                </Box>
              ))}
            </Box>
            <IconButton size='small' className='-mb-1 -mr-1 text-white' onClick={handleCopy}>
              <ContentCopyOutlined fontSize='small' />
            </IconButton>
          </Box>
        }
        placement='top'
        enterTouchDelay={0}
        arrow
      >
        <IconButton size='small'>
          <HelpOutlined fontSize='small' />
        </IconButton>
      </Tooltip>
    </Box>
  )
}
